import React, { useMemo } from "react";
import { Navigate, NavLink, Outlet, useLocation } from "react-router-dom";
import { LibraryIcon } from "../../Icons";
import {
  PAGE_SHELL,
  PAGE_HEADER_ROW,
  PAGE_SUBTITLE,
} from "../../ui/pageChrome";
import {
  LIBRARIES_NAV,
  LIBRARIES_DEFAULT_SECTION,
  getLibrariesSection,
} from "./librariesNav";
import {
  LIBRARIES_THEME,
  LIBRARIES_RAIL,
  librariesThemeCssVars,
} from "./librariesTheme";
import { getCarePlanSection } from "./CarePlanLibrary/carePlanSections";

/**
 * Libraries module shell — vertical sub-rail + content pane.
 * Access: Admin + OA (ROUTE_PERMISSIONS + RoleBasedRoute).
 * All catalog data is tenant-scoped when each sub-module is implemented.
 */
const LibrariesModule = () => {
  const location = useLocation();

  const { activeId, carePlanSectionKey } = useMemo(() => {
    const parts = location.pathname.split("/").filter(Boolean);
    const idx = parts.indexOf("libraries");
    const section = idx >= 0 ? parts[idx + 1] : "";
    const nested = idx >= 0 ? parts[idx + 2] : "";
    const id = LIBRARIES_NAV.some((item) => item.id === section)
      ? section
      : LIBRARIES_DEFAULT_SECTION;
    return {
      activeId: id,
      carePlanSectionKey:
        id === "care-plan-templates" && nested ? nested : null,
    };
  }, [location.pathname]);

  const activeSection = useMemo(() => {
    const base = getLibrariesSection(activeId);
    if (!carePlanSectionKey) return base;
    const sub = getCarePlanSection(carePlanSectionKey);
    if (!sub) return base;
    return {
      ...base,
      label: `${base.label} · ${sub.label}`,
      description: sub.description,
    };
  }, [activeId, carePlanSectionKey]);

  // /libraries with no section → first item
  if (
    location.pathname === "/libraries" ||
    location.pathname === "/libraries/"
  ) {
    return <Navigate to={`/libraries/${LIBRARIES_DEFAULT_SECTION}`} replace />;
  }

  return (
    <div className={PAGE_SHELL} style={librariesThemeCssVars()}>
      <div className={PAGE_HEADER_ROW}>
        <div>
          <h1 className={`text-2xl font-bold ${LIBRARIES_THEME.section.title}`}>
            Libraries
          </h1>
          <p className={PAGE_SUBTITLE}>
            Tenant master catalogs — manage reference data for this agency
          </p>
        </div>
      </div>

      <div
        className={`flex flex-col lg:flex-row bg-white rounded-xl border shadow-sm overflow-hidden min-h-[calc(100vh-11rem)] ${LIBRARIES_THEME.section.wrap}`}
      >
        {/* Vertical sub-sidebar */}
        <aside
          className={`w-full lg:w-56 shrink-0 border-b lg:border-b-0 lg:border-r ${LIBRARIES_RAIL.wrap}`}
        >
          <div
            className={`flex items-center gap-2 h-11 px-3.5 ${LIBRARIES_RAIL.header}`}
          >
            <LibraryIcon size={16} className="shrink-0 opacity-95" />
            <span className="text-sm font-semibold tracking-wide">Libraries</span>
          </div>
          <nav className="max-h-[40vh] lg:max-h-none overflow-y-auto">
            {LIBRARIES_NAV.map((item) => {
              const Icon = item.icon;
              const active = activeId === item.id;
              return (
                <NavLink
                  key={item.id}
                  to={`/libraries/${item.path}`}
                  className={`flex items-center gap-2.5 px-3.5 py-2.5 text-sm border-b transition-colors ${
                    active
                      ? LIBRARIES_RAIL.itemActive
                      : LIBRARIES_RAIL.item
                  }`}
                >
                  <Icon
                    size={15}
                    className={`shrink-0 ${
                      active
                        ? LIBRARIES_RAIL.itemIconActive
                        : LIBRARIES_RAIL.itemIcon
                    }`}
                  />
                  <span className="truncate">{item.label}</span>
                </NavLink>
              );
            })}
          </nav>
        </aside>

        {/* Section content */}
        <div className="flex-1 min-w-0 bg-slate-50/50 p-3 sm:p-4">
          <div className="mb-3">
            <h2 className={`text-lg font-semibold ${LIBRARIES_THEME.section.title}`}>
              {activeSection.label}
            </h2>
            <p className="text-sm text-slate-500 mt-0.5">
              {activeSection.description}
            </p>
          </div>
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default LibrariesModule;
